import { useState, useEffect } from "react";
import { useSession } from "../../context/AuthContext";
import { usePlans } from "../../context/PlanContext";
import { useNavigate } from "react-router-dom";
import FulfillmentDialog from "./FulfillmentDialog";
import AssistanceResolutionDialog from "./AssistanceResolutionDialog";
import api from "../../services/api";

interface ServiceItem {
  id: string;
  taskId: string;
  studentId: string;
  studentName: string;
  taskTitle: string;
  priority: string;
  status: "OPEN" | "ASSIGNED" | "COMPLETED" | "RESOLVED";
  submissionStatus: string | null;
  createdAt: string;
  type: "ASSISTANCE" | "EXPERT_JOB";
  taskType: "LINK" | "IMAGE" | "TEXT";
}

export default function ServiceDesk() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { getPlanByName } = usePlans();
  const role = (session?.user as any)?.role || "COACH";
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [historyItems, setHistoryItems] = useState<ServiceItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"LIVE" | "HISTORY">("LIVE");

  const getStatusDisplay = (item: ServiceItem) => {
    if (item.type === "EXPERT_JOB") {
      return { 
        label: item.status, 
        color: item.status === "OPEN" ? "bg-amber-500" : item.status === "ASSIGNED" ? "bg-primary" : "bg-emerald-500"
      };
    }

    // Assistance Mapping
    if (item.status === "OPEN") return { label: "Pending", color: "bg-amber-500" };
    if (item.status === "ASSIGNED") {
      if (item.submissionStatus === "REJECTED") return { label: "Rejected", color: "bg-red-500" };
      if (item.submissionStatus) return { label: "Submitted", color: "bg-violet-600 shadow-[0_0_8px_rgba(139,92,246,0.5)]" };
      return { label: "Assigned", color: "bg-blue-500" };
    }
    if (item.status === "RESOLVED") {
      if (item.submissionStatus === "REJECTED") return { label: "Rejected", color: "bg-red-500" };
      return { label: "Approved", color: "bg-emerald-500" };
    }
    return { label: item.status, color: "bg-slate-400" };
  };

  // Dialog States
  const [fulfillmentItem, setFulfillmentItem] = useState<ServiceItem | null>(null);
  const [assistanceResolutionItem, setAssistanceResolutionItem] = useState<ServiceItem | null>(null);

  const activeItems = activeTab === "LIVE" ? items : historyItems;

  const filteredItems = activeItems.filter(item => {
    const matchesRole = role === "MARKETING_ASSOCIATE" ? item.type === "EXPERT_JOB" : item.type === "ASSISTANCE";
    if (!matchesRole) return false;

    if (activeTab === "LIVE") {
      return item.status === "OPEN" || item.status === "ASSIGNED";
    } else {
      return item.status === "COMPLETED" || item.status === "RESOLVED";
    }
  });

  const fetchPool = async () => {
    try {
      const userId = session?.user?.id;
      const url = userId 
        ? `/service-pool?userId=${userId}`
        : `/service-pool`;
      
      const res = await api.get(url);
      const data = res.data;
      const pool = Array.isArray(data) ? data : [];
      const sorted = [...pool].sort((a, b) => {
        const pA = getPlanByName(a.priority)?.tier_level ?? 0;
        const pB = getPlanByName(b.priority)?.tier_level ?? 0;
        if (pA !== pB) return pB - pA;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(); 
      });
      setItems(sorted);
    } catch (err) {
      console.error("Failed to fetch pool:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const userId = session?.user?.id;
      const url = userId 
        ? `/service-pool/history?userId=${userId}`
        : `/service-pool/history`;
      
      const res = await api.get(url);
      const data = res.data;
      const pool = Array.isArray(data) ? data : [];
      setHistoryItems(pool);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPool();
    const interval = setInterval(fetchPool, 60000);
    return () => clearInterval(interval);
  }, [session]);

  useEffect(() => {
    if (activeTab === "HISTORY" && historyItems.length === 0) {
      fetchHistory();
    }
  }, [activeTab, session]);

  const handleClaim = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const item = items.find(i => i.id === id);
    if (!item) return;
    try {
      await api.patch(`/service-pool/${id}`, {
        status: "ASSIGNED", 
        type: item.type,
        staffId: session?.user?.id 
      });
      fetchPool();
    } catch (err) {
      console.error("Claim failed:", err);
    }
  };

  const handleResolveInitiate = (e: React.MouseEvent, item: ServiceItem) => {
    e.stopPropagation();
    if (item.type === "ASSISTANCE") {
      setAssistanceResolutionItem(item);
    } else {
      setFulfillmentItem(item);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      {/* Matrix Resolution System */}
      <FulfillmentDialog 
        isOpen={!!fulfillmentItem} 
        onClose={() => setFulfillmentItem(null)}
        item={fulfillmentItem}
        onSuccess={fetchPool}
      />

      <AssistanceResolutionDialog 
        isOpen={!!assistanceResolutionItem} 
        onClose={() => setAssistanceResolutionItem(null)}
        item={assistanceResolutionItem}
        onSuccess={fetchPool}
      />

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-outline-variant/10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tighter">
            {role === "MARKETING_ASSOCIATE" ? "Expert Job Pool" : "Assistance Desk"}
          </h2>
          <p className="text-on-surface-variant font-medium text-xs sm:text-sm">Designated workstation for student success and module execution.</p>
        </div>

        <div className="flex items-center bg-surface-container-high p-1 rounded-2xl border border-outline-variant/10 shadow-sm w-fit">
          <button 
            onClick={() => setActiveTab("LIVE")}
            className={`px-5 sm:px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              activeTab === "LIVE" ? "bg-on-surface text-surface shadow-lg font-bold" : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            Live Queue ({items.filter(i => (role === "MARKETING_ASSOCIATE" ? i.type === "EXPERT_JOB" : i.type === "ASSISTANCE") && (i.status === "OPEN" || i.status === "ASSIGNED")).length})
          </button>
          <button 
            onClick={() => setActiveTab("HISTORY")}
            className={`px-5 sm:px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              activeTab === "HISTORY" ? "bg-on-surface text-surface shadow-lg font-bold" : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            History
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
        {activeTab === "HISTORY" && historyLoading ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="bg-surface-container-low border border-outline-variant/10 rounded-[1.75rem] sm:rounded-[2.5rem] p-6 sm:p-7 h-[280px] animate-pulse" />
          ))
        ) : filteredItems.map((item) => {
          const sDisplay = getStatusDisplay(item);
          return (
            <div 
              key={item.id}
              onClick={() => navigate(`/tasks/${item.taskId}?userId=${item.studentId}`)}
              className={`bg-surface-container-low border rounded-[1.75rem] sm:rounded-[2.5rem] p-6 sm:p-7 transition-all hover:shadow-2xl hover:translate-y-[-4px] flex flex-col justify-between gap-6 border-t-8 h-full group cursor-pointer ${
                item.submissionStatus === "REJECTED" ? "border-red-500 bg-red-500/5" :
                item.submissionStatus ? "border-violet-600 bg-violet-600/5" :
                item.status === "OPEN" ? "border-amber-500 bg-amber-500/5" :
                item.status === "ASSIGNED" ? "border-blue-500 bg-blue-500/5" : 
                "border-emerald-500 bg-emerald-500/5"
              } ${activeTab === "HISTORY" ? "opacity-60 grayscale-[0.3]" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em]">#{item.id.slice(-6)}</span>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs text-on-surface-variant/40">schedule</span>
                  <span className="text-[10px] font-bold text-on-surface-variant/60">
                     {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              <div className="space-y-3 flex-1">
                <div className="space-y-0.5">
                  <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.15em] opacity-40">Assigned Task</p>
                  <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tighter leading-tight group-hover:text-primary transition-colors">
                    {item.taskTitle}
                  </h4>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="px-3 py-1 bg-surface-container-highest/50 text-on-surface-variant/60 rounded-full text-[9px] font-black uppercase tracking-widest border border-outline-variant/10 flex items-center gap-1.5 shadow-sm max-w-full overflow-hidden">
                    <span className="material-symbols-outlined text-[12px] shrink-0 opacity-40">person</span>
                    <span className="truncate">{item.studentName}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-outline-variant/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${sDisplay.color}`} />
                    <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/70">{sDisplay.label}</span>
                  </div>
                  <span 
                    className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter"
                    style={{
                      backgroundColor: getPlanByName(item.priority)?.icon_color ? `${getPlanByName(item.priority)?.icon_color}1A` : (
                        item.priority === "VIP" ? "rgba(6,182,212,0.1)" : 
                        item.priority === "PREMIUM" ? "rgba(245,158,11,0.1)" : 
                        "rgba(100,116,139,0.1)"
                      ),
                      color: getPlanByName(item.priority)?.icon_color || (
                        item.priority === "VIP" ? "#22d3ee" : 
                        item.priority === "PREMIUM" ? "#fbbf24" : 
                        "#94a3b8"
                      )
                    }}
                  >
                    {item.priority}
                  </span>
                </div>

                {item.status === "OPEN" ? (
                  <button 
                    onClick={(e) => handleClaim(e, item.id)}
                    className="w-full py-3.5 bg-amber-600 text-white rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl transition-all active:scale-[0.98] shadow-lg shadow-amber-600/20"
                  >
                    Pick Ticket
                  </button>
                ) : item.status === "ASSIGNED" ? (
                  <button 
                    onClick={(e) => handleResolveInitiate(e, item)}
                    className={`w-full py-3.5 text-white rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl transition-all shadow-lg active:scale-[0.98] ${
                      item.submissionStatus ? "bg-violet-600 shadow-violet-600/20" : "bg-blue-600 shadow-blue-600/20"
                    }`}
                  >
                    Mark {item.type === "EXPERT_JOB" ? "Complete" : "Resolved"}
                  </button>
                ) : (
                  <div className="w-full py-3.5 bg-surface-container-high text-on-surface-variant/40 rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 border border-outline-variant/10">
                    <span className="material-symbols-outlined text-sm">verified</span>
                    Action Finalized
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && !historyLoading && (
          <div className="col-span-full py-20 text-center space-y-4 bg-surface-container-lowest/50 rounded-[2rem] sm:rounded-[3rem] border-2 border-dashed border-outline-variant/10 px-6">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 animate-bounce">check_circle</span>
            <p className="text-on-surface-variant font-medium italic opacity-60 text-sm">Queue clear! All student requests have been handled.</p>
          </div>
        )}
      </div>
    </div>
  );
}
